import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Real toll crossings for AP39VF1179 sorted chronologically (Jun 16–19 2026)
const CROSSINGS = [
  { name: 'Linglapura',             coords: [13.44391,  76.13782],  time: '16 Jun, 10:56 PM' },
  { name: 'Rajatadripura Toll Plaza', coords: [13.319,   76.6963],  time: '17 Jun, 12:57 AM' },
  { name: 'Chokkenahalli Toll Plaza', coords: [13.302065,77.161956],time: '17 Jun, 2:42 AM'  },
  { name: 'Hulikunte',              coords: [13.271,    77.3475],   time: '17 Jun, 3:42 AM'  },
  { name: 'Nalluru Devanahalli',    coords: [13.206308, 77.765553], time: '17 Jun, 5:26 AM'  },
  { name: 'Mulbagal Toll Plaza',    coords: [13.148469, 78.290548], time: '17 Jun, 8:22 AM'  },
  { name: 'Nangli Toll Plaza',      coords: [13.194547, 78.545287], time: '17 Jun, 9:34 AM'  },
  { name: 'Mahasamudram',           coords: [13.194889, 79.017469], time: '17 Jun, 11:24 AM' },
  { name: 'Gadanki Toll Plaza',     coords: [13.478827, 79.188024], time: '17 Jun, 12:26 PM' },
  { name: 'Merlapaka Toll Plaza',   coords: [13.703536, 79.595097], time: '17 Jun, 1:37 PM'  },
  { name: 'Sullurpet Plaza NH-16',  coords: [13.744936, 79.990993], time: '18 Jun, 6:25 PM'  },
  { name: 'Budhanam Plaza NH-16',   coords: [14.059589, 79.894624], time: '19 Jun, 3:34 AM'  },
  { name: 'Nellore Plaza NH-16',    coords: [14.312218, 79.916296], time: '19 Jun, 4:14 AM'  },
  { name: 'Musunur Toll Plaza',     coords: [14.851945, 79.987847], time: '19 Jun, 6:23 AM'  },
  { name: 'Tangatur Toll Plaza',    coords: [15.358166, 80.040105], time: '19 Jun, 3:49 PM'  },
];

const VEHICLE = 'AP39VF****';
const TOTAL   = CROSSINGS.length;

// ── Phase durations (ms) ────────────────────────────────────────
const TYPING_SPEED  = 110;   // ms per character
const OTP_HOLD      = 2400;  // hold OTP screen before map
const STOP_INTERVAL = 900;   // ms between each stop appearing
const END_HOLD      = 3500;  // hold full map before restart

const TrackingDemoSection = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [phase, setPhase]             = useState('typing');  // typing | otp | map
  const [typedLen, setTypedLen]       = useState(0);
  const [visibleStops, setVisibleStops] = useState(0);

  const mapContainerRef = useRef(null);
  const leafletMapRef   = useRef(null);
  const markersRef      = useRef([]);
  const polylineRef     = useRef(null);

  // ── Load Leaflet once ──────────────────────────────────────────
  useEffect(() => {
    if (!document.getElementById('sh-demo-leaflet-css')) {
      const css = document.createElement('link');
      css.rel  = 'stylesheet';
      css.id   = 'sh-demo-leaflet-css';
      css.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(css);
    }
    if (!document.getElementById('sh-demo-leaflet-js')) {
      const js  = document.createElement('script');
      js.id     = 'sh-demo-leaflet-js';
      js.src    = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      js.async  = true;
      document.body.appendChild(js);
    }
  }, []);

  // ── Phase controller ───────────────────────────────────────────
  useEffect(() => {
    let t;
    if (phase === 'typing') {
      if (typedLen < VEHICLE.length) {
        t = setTimeout(() => setTypedLen(n => n + 1), TYPING_SPEED);
      } else {
        t = setTimeout(() => setPhase('otp'), 700);
      }
    } else if (phase === 'otp') {
      t = setTimeout(() => { setPhase('map'); setVisibleStops(0); }, OTP_HOLD);
    } else if (phase === 'map') {
      if (visibleStops < TOTAL) {
        t = setTimeout(() => setVisibleStops(n => n + 1), STOP_INTERVAL);
      } else {
        t = setTimeout(() => {
          setPhase('typing');
          setTypedLen(0);
          setVisibleStops(0);
        }, END_HOLD);
      }
    }
    return () => clearTimeout(t);
  }, [phase, typedLen, visibleStops]);

  // ── Init Leaflet map when map phase starts ─────────────────────
  useEffect(() => {
    if (phase !== 'map') return;

    const init = () => {
      if (!mapContainerRef.current || !window.L) return;
      const L = window.L;

      if (leafletMapRef.current) {
        leafletMapRef.current.remove();
        leafletMapRef.current = null;
      }
      markersRef.current  = [];
      polylineRef.current = null;

      const map = L.map(mapContainerRef.current, {
        zoomControl: false, attributionControl: false,
        scrollWheelZoom: false, dragging: false, doubleClickZoom: false,
      });

      L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
        maxZoom: 18, subdomains: 'abcd',
      }).addTo(map);

      // Fit to all coords upfront so map doesn't jump
      map.fitBounds(
        window.L.latLngBounds(CROSSINGS.map(c => c.coords)),
        { padding: [16, 16] }
      );

      leafletMapRef.current = map;
    };

    if (window.L) { init(); }
    else {
      const timer = setInterval(() => {
        if (window.L) { clearInterval(timer); init(); }
      }, 150);
      return () => clearInterval(timer);
    }

    return () => {
      if (leafletMapRef.current) {
        leafletMapRef.current.remove();
        leafletMapRef.current = null;
      }
    };
  }, [phase]);

  // ── Add one stop at a time ─────────────────────────────────────
  useEffect(() => {
    if (phase !== 'map' || visibleStops === 0) return;
    if (!leafletMapRef.current || !window.L) return;

    const L   = window.L;
    const map = leafletMapRef.current;
    const idx = visibleStops - 1; // 0-based
    const tc  = CROSSINGS[idx];
    const isLast = visibleStops === TOTAL;

    // Demote previous truck marker to numbered pin
    if (markersRef.current.length > 0) {
      const prevIdx  = visibleStops - 2;
      const prevSize = 22;
      const prevIcon = L.divIcon({
        className: '',
        html: `<div style="width:${prevSize}px;height:${prevSize}px;border-radius:50%;background:#f59e0b;border:2px solid #fcd34d;display:flex;align-items:center;justify-content:center;font-size:9px;font-weight:800;color:#0f172a;">${visibleStops - 1}</div>`,
        iconSize: [prevSize, prevSize], iconAnchor: [prevSize / 2, prevSize / 2],
      });
      markersRef.current[markersRef.current.length - 1].setIcon(prevIcon);
    }

    // New marker (truck or stop)
    const size = isLast ? 32 : 28;
    const icon = L.divIcon({
      className: '',
      html: `<div style="width:${size}px;height:${size}px;border-radius:50%;background:${isLast ? '#10b981' : '#f59e0b'};border:2px solid ${isLast ? '#34d399' : '#fcd34d'};display:flex;align-items:center;justify-content:center;font-size:${isLast ? '16px' : '11px'};font-weight:800;color:${isLast ? '#fff' : '#0f172a'};box-shadow:0 0 ${isLast ? '10px rgba(16,185,129,0.6)' : '4px rgba(245,158,11,0.4)'};">${isLast ? '🚛' : visibleStops}</div>`,
      iconSize: [size, size], iconAnchor: [size / 2, size / 2],
    });

    const marker = L.marker(tc.coords, { icon })
      .addTo(map)
      .bindPopup(
        L.popup({ maxWidth: 160 }).setContent(
          `<div style="padding:8px 10px;font-family:sans-serif;">
            <div style="color:#f59e0b;font-size:9px;font-weight:700;text-transform:uppercase;margin-bottom:2px;">${isLast ? '📍 Last seen' : 'Stop ' + visibleStops}</div>
            <div style="font-size:12px;font-weight:800;color:#1f2937;margin-bottom:2px;">${tc.name}</div>
            <div style="font-size:10px;color:#6b7280;">${tc.time}</div>
          </div>`
        )
      );
    markersRef.current.push(marker);

    // Redraw polyline
    if (polylineRef.current) { polylineRef.current.remove(); }
    const coords = CROSSINGS.slice(0, visibleStops).map(c => c.coords);
    if (coords.length > 1) {
      polylineRef.current = L.polyline(coords, {
        color: '#f59e0b', weight: 2, opacity: 0.85, dashArray: '7,5',
      }).addTo(map);
    }
  }, [visibleStops, phase]);

  // ── Render ─────────────────────────────────────────────────────
  return (
    <section style={{ background: '#0d1f35', padding: '64px 0' }}>
      <style>{`
        @keyframes sh-blink { 0%,100%{opacity:1} 50%{opacity:0} }
        .sh-demo-phone { box-shadow: 0 32px 64px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.05); }
        .sh-leaflet-demo .leaflet-popup-content-wrapper { background:#1e293b!important; border:1px solid #334155!important; border-radius:8px!important; box-shadow:0 4px 16px rgba(0,0,0,0.5)!important; padding:0!important; }
        .sh-leaflet-demo .leaflet-popup-content { margin:0!important; }
        .sh-leaflet-demo .leaflet-popup-tip { background:#1e293b!important; }
        .sh-leaflet-demo .leaflet-popup-close-button { color:#94a3b8!important; }
      `}</style>

      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '56px', flexWrap: 'wrap', justifyContent: 'center' }}>

          {/* ── Phone frame ── */}
          <div className="sh-demo-phone" style={{
            width: '272px', height: '544px',
            background: '#0f172a',
            borderRadius: '40px',
            border: '3px solid #1e293b',
            overflow: 'hidden',
            position: 'relative',
            flexShrink: 0,
          }}>
            {/* Status bar */}
            <div style={{ background: '#0f172a', padding: '10px 20px 6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: '#64748b', fontSize: '9px', fontWeight: 700 }}>9:41</span>
              <div style={{ width: '48px', height: '5px', background: '#1e293b', borderRadius: '3px' }} />
              <span style={{ color: '#64748b', fontSize: '9px' }}>●●●</span>
            </div>
            {/* URL bar */}
            <div style={{ background: '#1e293b', margin: '0 12px 8px', borderRadius: '8px', padding: '5px 10px', display: 'flex', alignItems: 'center', gap: '5px' }}>
              <span style={{ color: '#10b981', fontSize: '8px' }}>🔒</span>
              <span style={{ color: '#64748b', fontSize: '9px' }}>sugarhathi.com/track</span>
            </div>

            {/* ── Screen: Typing ── */}
            {phase === 'typing' && (
              <div style={{ padding: '20px 16px', textAlign: 'center' }}>
                <div style={{ fontSize: '32px', marginBottom: '6px' }}>🚛</div>
                <div style={{ color: '#f8fafc', fontSize: '13px', fontWeight: 800, marginBottom: '3px' }}>Track Your Sugar</div>
                <div style={{ color: '#475569', fontSize: '10px', marginBottom: '22px' }}>Enter vehicle number for live toll crossings</div>
                <div style={{
                  background: '#1e293b', borderRadius: '10px', padding: '12px 14px',
                  border: `2px solid ${typedLen === VEHICLE.length ? '#f59e0b' : '#334155'}`,
                  display: 'flex', alignItems: 'center', gap: '8px',
                  transition: 'border-color 0.3s',
                }}>
                  <span style={{ fontSize: '14px' }}>🚚</span>
                  <span style={{ color: '#f59e0b', fontSize: '15px', fontWeight: 800, letterSpacing: '1px', fontFamily: 'monospace' }}>
                    {VEHICLE.slice(0, typedLen)}
                    <span style={{ borderRight: '2px solid #f59e0b', marginLeft: '1px', animation: 'sh-blink 0.8s infinite' }}>&nbsp;</span>
                  </span>
                </div>
                {typedLen === VEHICLE.length && (
                  <div style={{ marginTop: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                    <span style={{ fontSize: '18px' }}>✅</span>
                    <span style={{ color: '#10b981', fontSize: '11px', fontWeight: 700 }}>FASTag Active</span>
                  </div>
                )}
              </div>
            )}

            {/* ── Screen: OTP ── */}
            {phase === 'otp' && (
              <div style={{ padding: '16px', textAlign: 'center' }}>
                <div style={{ fontSize: '26px', marginBottom: '6px' }}>💬</div>
                <div style={{ color: '#f8fafc', fontSize: '13px', fontWeight: 800, marginBottom: '3px' }}>Verify WhatsApp</div>
                <div style={{ color: '#475569', fontSize: '9px', marginBottom: '18px' }}>OTP sent to your WhatsApp number</div>
                <div style={{ background: '#1e293b', borderRadius: '8px', padding: '8px 10px', marginBottom: '14px', fontSize: '10px', color: '#64748b' }}>
                  ✓ OTP sent to <span style={{ color: '#f8fafc', fontWeight: 700 }}>+91 95XXX XXXX4</span>
                </div>
                <div style={{ display: 'flex', gap: '5px', justifyContent: 'center', marginBottom: '14px' }}>
                  {['1','7','1','2','9','4'].map((d, i) => (
                    <div key={i} style={{
                      width: '30px', height: '36px',
                      background: '#1e293b', borderRadius: '8px', border: '1px solid #334155',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#f8fafc', fontSize: '16px', fontWeight: 800,
                    }}>{d}</div>
                  ))}
                </div>
                <div style={{ background: '#10b981', borderRadius: '10px', padding: '10px', color: '#fff', fontSize: '10px', fontWeight: 700 }}>
                  ✓ Verify &amp; View Live Tracking
                </div>
              </div>
            )}

            {/* ── Screen: Map ── */}
            {phase === 'map' && (
              <div style={{ position: 'absolute', inset: 0, top: '60px', display: 'flex', flexDirection: 'column' }}>
                {/* Vehicle header */}
                <div style={{ background: '#0f172a', padding: '6px 12px', borderBottom: '1px solid #1e293b', flexShrink: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <div style={{ color: '#f59e0b', fontSize: '13px', fontWeight: 900, letterSpacing: '0.5px', fontFamily: 'monospace' }}>AP39VF****</div>
                      <div style={{ color: '#10b981', fontSize: '8px', fontWeight: 700 }}>● Live · Auto-refreshes every 45 min</div>
                    </div>
                    <div style={{ background: '#1e4a72', borderRadius: '6px', padding: '3px 6px', fontSize: '8px', color: '#fff', fontWeight: 700 }}>
                      ✓ Platform Tracking
                    </div>
                  </div>
                </div>
                {/* Leaflet map */}
                <div
                  ref={mapContainerRef}
                  className="sh-leaflet-demo"
                  style={{ flex: 1, minHeight: 0, background: '#e5e7eb' }}
                />
                {/* Stats footer */}
                <div style={{ background: '#0f172a', display: 'flex', justifyContent: 'space-around', padding: '7px 0', borderTop: '1px solid #1e293b', flexShrink: 0 }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ color: '#475569', fontSize: '7px', textTransform: 'uppercase', fontWeight: 700 }}>Plazas</div>
                    <div style={{ color: '#f8fafc', fontSize: '14px', fontWeight: 900 }}>{visibleStops}</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ color: '#475569', fontSize: '7px', textTransform: 'uppercase', fontWeight: 700 }}>First</div>
                    <div style={{ color: '#f8fafc', fontSize: '11px', fontWeight: 700 }}>16 Jun</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ color: '#475569', fontSize: '7px', textTransform: 'uppercase', fontWeight: 700 }}>Latest</div>
                    <div style={{ color: '#10b981', fontSize: '11px', fontWeight: 700 }}>19 Jun</div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── Right: Text ── */}
          <div style={{ flex: 1, minWidth: '260px', maxWidth: '440px' }}>
            <div style={{ color: '#f59e0b', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '12px' }}>
              Live FASTag Tracking
            </div>
            <h2 style={{ color: '#f8fafc', fontSize: '30px', fontWeight: 900, lineHeight: 1.15, marginBottom: '16px' }}>
              Know where your<br />
              sugar is —{' '}
              <span style={{ color: '#f59e0b' }}>at every toll plaza.</span>
            </h2>
            <p style={{ color: '#94a3b8', fontSize: '14px', lineHeight: 1.75, marginBottom: '28px' }}>
              Real-time FASTag toll crossing data from NHAI — directly linked to your booking.
              Every plaza, every timestamp, on one map. No calls, no guessing.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '28px' }}>
              {[
                ['🏛', 'Government FASTag data', 'Sourced directly from NPCI / NHAI'],
                ['📍', 'Live toll crossing map', 'Dark map with numbered pins and route polyline'],
                ['🔒', 'OTP verified access',   'Only authorised viewers can track your vehicle'],
              ].map(([icon, title, desc]) => (
                <div key={title} style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                  <div style={{ fontSize: '20px', marginTop: '1px', flexShrink: 0 }}>{icon}</div>
                  <div>
                    <div style={{ color: '#f8fafc', fontSize: '13px', fontWeight: 700, marginBottom: '2px' }}>{title}</div>
                    <div style={{ color: '#475569', fontSize: '12px' }}>{desc}</div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '20px' }}>
              <button
                onClick={() => navigate('/sugar-marketplace-dashboard')}
                style={{ background: '#f59e0b', color: '#0f172a', border: 'none', borderRadius: '10px', padding: '10px 18px', fontSize: '13px', fontWeight: 800, cursor: 'pointer' }}
              >
                {t('hero.browseSugarOffers', 'Browse Sugar Offers')}
              </button>
              <button
                onClick={() => navigate('/truck-marketplace')}
                style={{ background: 'transparent', color: '#f8fafc', border: '1px solid #334155', borderRadius: '10px', padding: '10px 18px', fontSize: '13px', fontWeight: 800, cursor: 'pointer' }}
              >
                {t('hero.browseVerifiedTrucks', 'Browse Verified Trucks')}
              </button>
            </div>
            <div style={{ color: '#f8fafc', fontSize: '12px', fontWeight: 600 }}>
              *₹199 one-time fee · Unlimited tracking for 4 days
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default TrackingDemoSection;
